/**
 * The browser half of signing in. Story VC-017.
 *
 * Every decision this makes is `oidc.js`', which is tested. What is here is
 * the part that touches the tab: redirecting, keeping the verifier across
 * one navigation, and exchanging a code. It is untested for the reason
 * recorded since VC-004 — this container has no browser — and it is written
 * to be as close to nothing as it can be.
 *
 * # Where things are kept, and for how long
 *
 * The verifier and the state live in `sessionStorage`: they are wanted
 * across exactly one navigation, and a tab that is closed mid-login should
 * not leave them behind. The tokens live in memory only — a refresh of the
 * page signs in again through the provider, which is silent when its own
 * session is live, and nothing this client holds outlives the tab.
 */

import {
  afterRefusal,
  authorizeUrl,
  challengeFor,
  createVerifier,
  exchangeBody,
  logoutUrl,
  needsRefresh,
  readCallback,
  refreshBody,
  tokensFrom,
} from "./oidc.js";

const VERIFIER_KEY = "voiceagent.pkce.verifier";
const STATE_KEY = "voiceagent.pkce.state";

const now = () => Math.floor(Date.now() / 1000);

/**
 * Reads the identity provider's coordinates from the build's environment.
 *
 * `null` when none is configured, which is the development path: the page
 * then runs on the static token it has always used, and nothing about
 * signing in appears.
 */
export function configFrom(env) {
  const domain = env.VITE_COGNITO_DOMAIN;
  const clientId = env.VITE_COGNITO_CLIENT_ID;
  if (!domain || !clientId) return null;
  return {
    domain,
    clientId,
    // The provider matches this against the app client's list exactly, so
    // it is the origin and a trailing slash, never `location.href`.
    redirectUri: env.VITE_REDIRECT_URI ?? `${location.origin}/`,
  };
}

/** Sends the browser to the provider's pages. */
export async function beginSignIn(config) {
  const verifier = createVerifier();
  const state = createVerifier();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  location.assign(
    authorizeUrl(config, { challenge: await challengeFor(verifier), state }),
  );
}

/**
 * Completes a sign-in if this load is the redirect back.
 *
 * Returns the tokens, or `null` when this is an ordinary load. Throws when
 * the provider said no, or when the state does not match — which means the
 * response is not an answer to a request this tab made.
 */
export async function completeSignIn(config) {
  const callback = readCallback(location.search);
  if (callback.kind === "none") return null;

  const expected = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  // The code is in the address bar and is single-use; leaving it there
  // means a refresh retries an exchange that cannot succeed twice.
  history.replaceState(null, "", location.pathname);

  if (callback.kind === "error") {
    throw new Error(`sign-in was refused: ${callback.error}`);
  }
  if (!expected || callback.state !== expected || !verifier) {
    throw new Error("sign-in did not answer a request this tab made");
  }

  return exchange(config, exchangeBody(config, { code: callback.code, verifier }));
}

/** The access token to use now, refreshing first if it is close to expiry. */
export async function freshToken(config, tokens, replace) {
  if (!needsRefresh(tokens, now())) return tokens.accessToken;
  const renewed = await exchange(config, refreshBody(config, tokens.refreshToken));
  // Cognito does not return a refresh token on a refresh, so the one in
  // hand is carried forward; dropping it would end the session an hour in.
  const carried = { ...renewed, refreshToken: renewed.refreshToken ?? tokens.refreshToken };
  replace(carried);
  return carried.accessToken;
}

/** What to do about a refused call. `oidc.js` decides; this only names it. */
export const decideRefusal = afterRefusal;

/** Ends both sessions: this tab's, and the provider's. */
export function signOut(config) {
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  location.assign(logoutUrl(config));
}

async function exchange(config, body) {
  const response = await fetch(new URL("/oauth2/token", config.domain), {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!response.ok) {
    // The provider's body is not read into the message: it is
    // attacker-influenceable text that would be rendered.
    throw new Error(`the identity provider refused the exchange (${response.status})`);
  }
  return tokensFrom(await response.json(), now());
}
